import _ from 'underscore';
import Promise from 'bluebird';
import { DataSourceState } from '@bingads-webui-react/image-picker'; // eslint-disable-line
import { StockImageDataService } from '@bingads-webui-campaign/stock-image-data-service';
import { validateImageWithDimension } from '../../util/image-size-validation';
import C from '../../constants';

class StockPhotoServiceError extends Error {
  constructor(e) {
    super('StockPhotoServiceError');

    if (e.errors && e.errors.length > 0) {
      this.innerError = e.errors[0].Code;
    }
  }

  getInnerError() {
    return this.innerError;
  }
}

class StockPhotoDataSource {
  constructor(options) {
    this.currentData = [];
    this.currentQuery = '';
    this.hasMore = false;

    this.odataService = new StockImageDataService({
      cid: options.cid,
      aid: options.aid,
      oDataConfig: options.oDataConfig,
      customParams: options.customParams,
      controller: options.controller,
      top: 20,
      skip: 0,
    });

    this.filter = options.filter;
    this.filterQuery = {};

    this.pageSize = 20;
    this.isMultimediaAd = options.isMultimediaAd || false;
  }

  subscribe(callback) {
    this.callback = callback;
  }

  unsubscribe() {
    this.callback = null;
  }

  excuteCallBackIfExsit(status) {
    if (this.callback) {
      this.callback(status);
    }
  }

  loadMore() {
    this.currentStart += this.pageSize;

    return this.searchInternalAsync();
  }

  reset() {
    this.currentData = [];

    this.excuteCallBackIfExsit(DataSourceState.Done);
  }

  updateFilterQuery(filterQuery) {
    this.filterQuery = filterQuery;

    if (this.currentQuery && !_.isEmpty(this.currentQuery.trim())) {
      this.reset();

      this.searchInternalAsync();
    }
  }

  searchAsync(query) {
    this.reset();

    if (!query || _.isEmpty(query.trim())) {
      return Promise.resolve();
    }

    this.currentQuery = query;
    this.currentStart = 0;

    return this.searchInternalAsync();
  }

  searchInternalAsync() {
    this.excuteCallBackIfExsit(DataSourceState.Loading);
    const culture = _.get(window, ['globalLegacyI18n', 'context', 'culture']);

    return this.odataService.getStockImages({
      customQueries: _.extend({
        appName: 'CampaignUI',
        $search: this.currentQuery,
        $skip: this.currentStart,
        $top: this.pageSize,
        language: culture,
        includevertical: this.isMultimediaAd,
      }, this.filterQuery),
    }).then((data) => {
      this.hasMore = data['@odata.count'] > data.value.length + this.currentStart;

      const filteredData = _.filter(data.value, this.filter);

      const moreData = _.map(filteredData, item => ({
        id: parseInt(item.Id, 10),
        src: item.Thumbnail.Url,
        width: item.Thumbnail.Dimensions.Width,
        height: item.Thumbnail.Dimensions.Height,
        altText: item.Description || '',
        textFields: [],
      }));

      this.currentData = this.currentData.concat(moreData);
    }).catch((e) => { throw e; })
      .finally(() => { this.excuteCallBackIfExsit(DataSourceState.Done); });
  }

  SaveAsync(options) {
    return this.odataService.saveStockImage(options)
      .catch((e) => {
        throw new StockPhotoServiceError(e);
      });
  }

  getData() {
    return { hasMore: this.hasMore, data: this.currentData };
  }
}

const initializeStockPhotoDataSource = (
  cid,
  aid,
  oDataConfig,
  imageType,
  isMultimediaAd = false,
  imageOption = C.defaultImageOption
) => {
  const stockPhotoDataSource = new StockPhotoDataSource({
    cid,
    aid,
    oDataConfig,
    controller: 'StockImages',
    top: 20,
    skip: 0,
    isMultimediaAd,
    filter: ((item) => {
      const { Width, Height } = item.Preview.Dimensions;

      if (validateImageWithDimension(
        Width, Height,
        imageType, isMultimediaAd, imageOption
      ) === null) {
        return true;
      }

      return false;
    }),
  });

  return stockPhotoDataSource;
};

export { StockPhotoServiceError, StockPhotoDataSource, initializeStockPhotoDataSource };
